/* eslint-disable camelcase, react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format_year, time_format } from '../../../../constants/index';

const PAGE_LIMIT = 20;
const OUTLINE_BUTTON =
  'px-4 py-2 border border-[#4332df] rounded-md text-sm text-[#4332df] bg-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

const humanize_event_name = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

const get_event_title = (event, is_cms_action) => {
  if (is_cms_action) return humanize_event_name(event.action) || 'CMS Action';
  return humanize_event_name(event.event_name) || 'Student Event';
};

const display_value = (value) => value || '—';

const format_field_value = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// Convert arbitrary event payloads into readable paths while retaining every leaf value.
const flatten_object = (value, prefix = '', rows = []) => {
  if (Array.isArray(value)) {
    if (!value.length && prefix) rows.push([prefix, '—']);
    value.forEach((item, index) => flatten_object(item, `${prefix}[${index + 1}]`, rows));
  } else if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length && prefix) rows.push([prefix, '—']);
    entries.forEach(([key, nested_value]) => {
      const label = humanize_event_name(key);
      flatten_object(nested_value, prefix ? `${prefix} › ${label}` : label, rows);
    });
  } else if (prefix) {
    rows.push([prefix, format_field_value(value)]);
  }
  return rows;
};

function DetailTable({ title, value }) {
  const rows = flatten_object(value);
  return (
    <section className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      {title ? (
        <h5 className="m-0 px-4 py-2.5 text-sm font-semibold text-slate-800 bg-slate-50 border-b border-slate-200">
          {title}
        </h5>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 bg-white">
              <th className="w-[32%] px-4 py-2 font-semibold border-b border-slate-100">Field</th>
              <th className="px-4 py-2 font-semibold border-b border-slate-100">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map(([field, field_value]) => (
                <tr className="align-top even:bg-slate-50/60" key={field}>
                  <td className="px-4 py-2.5 text-slate-600 font-medium border-b border-slate-100">
                    {field}
                  </td>
                  <td className="px-4 py-2.5 text-slate-900 break-all border-b border-slate-100">
                    {field_value}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-3 text-slate-500" colSpan="2">
                  No data recorded
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

DetailTable.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.any)])
};

DetailTable.defaultProps = {
  title: '',
  value: {}
};

function CollapsibleDetail({ title, value }) {
  const field_count = flatten_object(value).length;

  return (
    <details className="group overflow-hidden rounded-lg border border-slate-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4332df]">
        <span>{title}</span>
        <span className="flex items-center gap-3 text-xs font-medium text-slate-500">
          {field_count} {field_count === 1 ? 'field' : 'fields'}
          <span
            aria-hidden="true"
            className="text-base text-[#4332df] transition-transform group-open:rotate-180"
          >
            ⌄
          </span>
        </span>
      </summary>
      <div className="border-t border-slate-200">
        <DetailTable value={value} />
      </div>
    </details>
  );
}

CollapsibleDetail.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.any)])
};

CollapsibleDetail.defaultProps = {
  value: {}
};

// Keep common scalar values visible and put large nested payloads behind named sections.
const partition_event_data = (data = {}) => {
  const overview = {};
  const sections = [];
  Object.entries(data).forEach(([key, value]) => {
    if (value && typeof value === 'object') sections.push([key, value]);
    else overview[key] = value;
  });
  return { overview, sections };
};

function EventDetails({ event, is_cms_action }) {
  const metadata_entries = Object.entries(event.metadata);

  if (!is_cms_action) {
    const { overview, sections } = partition_event_data(event.data);
    return (
      <div className="grid gap-3 p-4 border-t border-slate-200 bg-slate-50">
        {Object.keys(overview).length ? <DetailTable title="Overview" value={overview} /> : null}
        {sections.map(([key, value]) => (
          <CollapsibleDetail key={key} title={humanize_event_name(key)} value={value} />
        ))}
        {!Object.keys(overview).length && !sections.length ? (
          <p className="m-0 text-sm text-slate-500">No event data recorded</p>
        ) : null}
      </div>
    );
  }

  // what_happened is the producer's one-line sentence for rows that create or send something and
  // so have no before/after to show (PROD-9247); row-changing actions leave it empty.
  const action_details = {
    action: humanize_event_name(event.action),
    what_happened: event.summary,
    performed_by: event.performed_by?.name,
    cms_user_uuid: event.performed_by?.uuid,
    source: event.source,
    reason: event.reason,
    request_id: event.request_id
  };

  return (
    <div className="grid gap-3 p-4 border-t border-slate-200 bg-slate-50">
      <DetailTable title="Action summary" value={action_details} />
      <CollapsibleDetail title="Request" value={event.received_payload} />
      <CollapsibleDetail title="Before action" value={event.pre_execution_object} />
      <CollapsibleDetail title="After action" value={event.post_execution_object} />
      {metadata_entries.length ? (
        <CollapsibleDetail title="Metadata" value={event.metadata} />
      ) : null}
    </div>
  );
}

EventDetails.propTypes = {
  event: PropTypes.shape({}).isRequired,
  is_cms_action: PropTypes.bool.isRequired
};

// The rows render in the reader's timezone, so a picked day is a window that straddles two UTC
// dates. Send both ends as instants and let the reader's day mean the reader's day (PROD-9256).
// The digits goserver sends are UTC, whatever offset is glued to them.
//
// range_hour goes into ClickHouse as a naive string (timeline/events.go), so ClickHouse parses it in
// its own zone and the driver hands it back stamped +05:30 over a UTC wall clock: an action taken at
// 12:32 PM IST arrives as 2026-09-18T07:02:44+05:30. Taking that offset at face value drew every row
// 5h30m early, with the header timezone's own label on it, which is how the tab read "07:02 AM IST"
// for something done at 12:32 PM. Reading the wall clock back as UTC undoes the parse, and then the
// header dropdown converts a real instant. Measured on testenv1 18 Sep 2026 against two known clocks.
//
// A timestamp that already ends in Z survives this unchanged, so it stays correct if goserver is
// ever fixed at the source.
const event_instant = (timestamp) =>
  moment.utc(moment.parseZone(timestamp).format('YYYY-MM-DDTHH:mm:ss.SSS'));

const day_bounds_utc = (from_date, to_date, iana_timezone) => ({
  from: from_date ? moment.tz(from_date, 'YYYY-MM-DD', iana_timezone).toISOString() : '',
  // Exclusive, so the whole of the closing day is inside the range.
  to: to_date ? moment.tz(to_date, 'YYYY-MM-DD', iana_timezone).add(1, 'day').toISOString() : ''
});

function StudentActivity({ student_uuid, iana_timezone }) {
  const [category, set_category] = useState('cms_actions');
  const [events, set_events] = useState([]);
  const [pagination, set_pagination] = useState({ page: 1, limit: PAGE_LIMIT, has_more: false });
  const [loading, set_loading] = useState(false);
  const [loading_more, set_loading_more] = useState(false);
  const [error, set_error] = useState('');
  const [selected_event_id, set_selected_event_id] = useState('');
  const [search_text, set_search_text] = useState('');
  // Server-side filters (PROD-9256). The search box above filters the page already loaded; these
  // filter the read itself, which is the only way to find an action 40 pages back.
  const [selected_actions, set_selected_actions] = useState([]);
  const [from_date, set_from_date] = useState('');
  const [to_date, set_to_date] = useState('');
  const [action_options, set_action_options] = useState([]);
  const [actions_open, set_actions_open] = useState(false);
  // Each tab, refresh, or pagination request supersedes older requests. This prevents
  // a slow response from replacing the currently selected timeline.
  const request_id_ref = useRef(0);

  const load_events = useCallback(
    (selected_category, filters = {}, page = 1, append = false) => {
      if (!student_uuid) return;
      const request_id = request_id_ref.current + 1;
      request_id_ref.current = request_id;
      set_error('');
      if (append) set_loading_more(true);
      else set_loading(true);

      const query = new URLSearchParams({
        category: selected_category,
        page: String(page),
        limit: String(PAGE_LIMIT)
      });
      // Omitted rather than sent empty: an empty action list means every action, and the
      // endpoint rejects a malformed date rather than ignoring it.
      if (filters.actions?.length) query.set('action', filters.actions.join(','));
      const bounds = day_bounds_utc(filters.from, filters.to, iana_timezone);
      if (bounds.from) query.set('from', bounds.from);
      if (bounds.to) query.set('to', bounds.to);
      const url = get_api_url(
        `/v2/cms_user/students/${encodeURIComponent(student_uuid)}/events?${query.toString()}`
      );

      get_api(url, true)
        .then((response) => {
          if (request_id !== request_id_ref.current) return;
          const payload = response.data.data;
          const next_events = payload.events;
          // Pagination appends; tab changes and refreshes replace the visible timeline.
          set_events((current) => (append ? [...current, ...next_events] : next_events));
          set_pagination(payload.pagination);
        })
        .catch(() => {
          if (request_id !== request_id_ref.current) return;
          set_error('Could not load student activity. Please try again.');
          if (!append) set_events([]);
        })
        .finally(() => {
          if (request_id !== request_id_ref.current) return;
          set_loading(false);
          set_loading_more(false);
        });
    },
    [student_uuid, iana_timezone]
  );

  // Load the options for the action filter. They come from this student's own timeline rather
  // than from a fixed list: the action registry lives in two repos and neither holds all of it,
  // and what Care wants to filter by is what actually happened to this student.
  const load_action_options = useCallback(
    (selected_category, filters = {}) => {
      if (!student_uuid) return;
      const query = new URLSearchParams({ category: selected_category });
      const bounds = day_bounds_utc(filters.from, filters.to, iana_timezone);
      if (bounds.from) query.set('from', bounds.from);
      if (bounds.to) query.set('to', bounds.to);
      const url = get_api_url(
        `/v2/cms_user/students/${encodeURIComponent(student_uuid)}/events/actions?${query.toString()}`
      );

      get_api(url, true)
        .then((response) => set_action_options(response.data.data.actions || []))
        // A missing filter list is not worth an error banner: the timeline itself still loads.
        .catch(() => set_action_options([]));
    },
    [student_uuid, iana_timezone]
  );

  useEffect(() => {
    // Expanded state and filters belong to the previous category and must not leak across tabs.
    set_events([]);
    set_selected_event_id('');
    set_search_text('');
    set_selected_actions([]);
    set_from_date('');
    set_to_date('');
    set_actions_open(false);
    load_events(category);
    load_action_options(category);
  }, [category, load_events, load_action_options]);

  const select_category = (next_category) => {
    if (next_category !== category) set_category(next_category);
  };

  // Every filter change is a new read from page 1; appending to a differently filtered list
  // would interleave two result sets.
  const apply_filters = (next) => {
    const filters = {
      actions: next.actions ?? selected_actions,
      from: next.from ?? from_date,
      to: next.to ?? to_date
    };

    if (next.actions !== undefined) set_selected_actions(next.actions);
    if (next.from !== undefined) set_from_date(next.from);
    if (next.to !== undefined) set_to_date(next.to);

    set_selected_event_id('');
    load_events(category, filters);
    // A narrowed window offers fewer actions; a widened one offers more.
    if (next.from !== undefined || next.to !== undefined) load_action_options(category, filters);
  };

  const toggle_action = (action) => {
    apply_filters({
      actions: selected_actions.includes(action)
        ? selected_actions.filter((value) => value !== action)
        : [...selected_actions, action]
    });
  };

  const clear_filters = () => apply_filters({ actions: [], from: '', to: '' });

  const current_filters = { actions: selected_actions, from: from_date, to: to_date };
  const filters_active = selected_actions.length > 0 || Boolean(from_date) || Boolean(to_date);

  const format_event_date = (timestamp) =>
    event_instant(timestamp).tz(iana_timezone).format(day_format_year);
  const format_event_time = (timestamp) =>
    event_instant(timestamp).tz(iana_timezone).format(time_format);

  const is_cms_action = category === 'cms_actions';
  const empty_message = is_cms_action
    ? 'No CMS actions recorded for this student.'
    : 'No student events recorded for this student.';
  const normalized_search = search_text.trim().toLowerCase();
  // Search summaries and payload values so operators can find an event without opening each row.
  const filtered_events = normalized_search
    ? events.filter((event) =>
        [
          event.action,
          event.event_name,
          event.category,
          event.source,
          event.reason,
          event.summary,
          event.performed_by?.name,
          event.performed_by?.uuid,
          JSON.stringify(event.data || {}),
          JSON.stringify(event.received_payload || {}),
          JSON.stringify(event.pre_execution_object || {}),
          JSON.stringify(event.post_execution_object || {}),
          JSON.stringify(event.metadata || {})
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalized_search)
      )
    : events;
  const selected_event = filtered_events.find((event) => event.id === selected_event_id);
  const selected_event_title = selected_event ? get_event_title(selected_event, is_cms_action) : '';
  const event_count_label = normalized_search
    ? `${filtered_events.length} of ${events.length} events`
    : `${events.length} ${events.length === 1 ? 'event' : 'events'}`;

  return (
    <section
      className="flex h-[calc(92vh-72px)] w-full flex-col overflow-y-auto bg-white text-slate-900 lg:overflow-hidden"
      aria-labelledby="student-activity-title"
    >
      <div className="sticky top-0 z-20 shrink-0 border-b border-[#e4e7ec] bg-white px-1 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 lg:flex lg:items-baseline lg:gap-3">
            <h2 id="student-activity-title" className="m-0 text-xl font-bold text-[#172033]">
              Student Activity
            </h2>
            <p className="mb-0 mt-1 text-sm text-[#667085] lg:mt-0">
              Review recorded actions and events.
            </p>
          </div>
          <button
            type="button"
            className="!flex !items-center !gap-1.5 !rounded-md !border !border-[#b9b2ff] !bg-white !px-3 !py-1.5 text-sm !font-semibold !text-[#4332df] hover:!bg-[#f7f6ff] disabled:!cursor-not-allowed disabled:!opacity-50"
            onClick={() => load_events(category, current_filters)}
            disabled={loading || loading_more}
          >
            <span aria-hidden="true" className="text-base leading-none">
              ↻
            </span>
            Refresh
          </button>
        </div>
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-[#e4e7ec] bg-[#f8fafc] p-2 lg:flex-row lg:items-center">
          <div
            className="inline-flex self-start gap-1 rounded-md bg-[#eaecf0] p-1"
            role="tablist"
            aria-label="Student activity type"
          >
            <button
              type="button"
              role="tab"
              aria-selected={is_cms_action}
              className={`!border px-4 py-1.5 rounded-md text-sm font-semibold ${
                is_cms_action
                  ? '!border-[#4332df] !bg-[#4332df] !text-white shadow-sm'
                  : '!border-[#cbd5e1] !bg-white !text-[#334155]'
              }`}
              onClick={() => select_category('cms_actions')}
            >
              CMS Actions
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!is_cms_action}
              className={`!border px-4 py-1.5 rounded-md text-sm font-semibold ${
                !is_cms_action
                  ? '!border-[#4332df] !bg-[#4332df] !text-white shadow-sm'
                  : '!border-[#cbd5e1] !bg-white !text-[#334155]'
              }`}
              onClick={() => select_category('other')}
            >
              Student Events
            </button>
          </div>
          {events.length || filters_active ? (
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center lg:justify-end">
              <label className="relative block w-full max-w-lg" htmlFor="student-activity-search">
                <span className="sr-only">Search student activity</span>
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="m17 17-3.6-3.6m1.93-4.23a6.17 6.17 0 1 1-12.33 0 6.17 6.17 0 0 1 12.33 0Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  id="student-activity-search"
                  type="search"
                  value={search_text}
                  onChange={(search_event) => set_search_text(search_event.target.value)}
                  placeholder="Search events, actor, source, or details"
                  className="w-full !rounded-md !border !border-[#d0d5dd] !bg-white py-1.5 pl-9 pr-20 text-sm !text-[#172033] placeholder:!text-[#98a2b3] focus:!border-[#4332df] focus:!outline-none focus:!ring-1 focus:!ring-[#4332df]"
                />
                {search_text ? (
                  <button
                    type="button"
                    className="!absolute !right-2 !top-1/2 !-translate-y-1/2 !border-0 !bg-transparent !px-2 !py-1 text-xs font-semibold text-[#4332df]"
                    onClick={() => set_search_text('')}
                  >
                    Clear
                  </button>
                ) : null}
              </label>
              <p
                className="m-0 min-w-[72px] text-xs font-medium text-[#667085] lg:text-right"
                aria-live="polite"
              >
                {event_count_label}
              </p>
            </div>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="relative">
            <button
              type="button"
              aria-expanded={actions_open}
              aria-haspopup="true"
              className="!flex !items-center !gap-2 !rounded-md !border !border-[#d0d5dd] !bg-white !px-3 !py-1.5 text-sm !font-medium !text-[#344054] hover:!border-[#4332df]"
              onClick={() => set_actions_open((open) => !open)}
              disabled={!action_options.length && !selected_actions.length}
            >
              {selected_actions.length
                ? `${selected_actions.length} action filters`
                : 'All actions'}
              <span aria-hidden="true" className="text-xs text-[#667085]">
                ⌄
              </span>
            </button>
            {actions_open ? (
              <div
                className="absolute left-0 z-30 mt-1 max-h-72 w-80 overflow-y-auto rounded-md border border-[#e4e7ec] bg-white p-1 shadow-lg"
                role="group"
                aria-label="Filter by action"
              >
                {action_options.length ? (
                  action_options.map((option) => (
                    <label
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-[#344054] hover:bg-[#f8fafc]"
                      htmlFor={`student-activity-action-${option.action}`}
                      key={option.action}
                    >
                      <input
                        id={`student-activity-action-${option.action}`}
                        type="checkbox"
                        checked={selected_actions.includes(option.action)}
                        onChange={() => toggle_action(option.action)}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {humanize_event_name(option.action)}
                      </span>
                      <span className="shrink-0 text-xs text-[#667085]">{option.count}</span>
                    </label>
                  ))
                ) : (
                  <p className="m-0 px-2 py-3 text-sm text-[#667085]">
                    No actions recorded in this range.
                  </p>
                )}
              </div>
            ) : null}
          </div>
          <label
            className="flex items-center gap-1.5 text-xs font-medium text-[#667085]"
            htmlFor="student-activity-from"
          >
            From
            <input
              id="student-activity-from"
              type="date"
              value={from_date}
              max={to_date || undefined}
              onChange={(date_event) => apply_filters({ from: date_event.target.value })}
              className="!rounded-md !border !border-[#d0d5dd] !bg-white !px-2 !py-1 text-sm !text-[#172033]"
            />
          </label>
          <label
            className="flex items-center gap-1.5 text-xs font-medium text-[#667085]"
            htmlFor="student-activity-to"
          >
            To
            <input
              id="student-activity-to"
              type="date"
              value={to_date}
              min={from_date || undefined}
              onChange={(date_event) => apply_filters({ to: date_event.target.value })}
              className="!rounded-md !border !border-[#d0d5dd] !bg-white !px-2 !py-1 text-sm !text-[#172033]"
            />
          </label>
          {filters_active ? (
            <button
              type="button"
              className="!border-0 !bg-transparent !px-1 text-xs !font-semibold !text-[#4332df]"
              onClick={clear_filters}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading student activity…</div>
      ) : null}
      {!loading && error ? (
        <div className="py-10 text-center text-red-700" role="alert">
          <p className="mb-3">{error}</p>
          <button
            className={OUTLINE_BUTTON}
            type="button"
            onClick={() => load_events(category, current_filters)}
          >
            Retry
          </button>
        </div>
      ) : null}
      {!loading && !error && events.length === 0 ? (
        <div className="py-16 text-center text-slate-500">
          <p className="mb-3">
            {filters_active ? 'No activity matches these filters.' : empty_message}
          </p>
          {filters_active ? (
            <button className={OUTLINE_BUTTON} type="button" onClick={clear_filters}>
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && !error && events.length && !filtered_events.length ? (
        <div className="py-16 text-center text-slate-500">
          <p className="mb-3">No activity matches “{search_text}”.</p>
          <button className={OUTLINE_BUTTON} type="button" onClick={() => set_search_text('')}>
            Clear search
          </button>
        </div>
      ) : null}

      {!loading && !error && filtered_events.length ? (
        <div className="mt-3 grid min-h-0 flex-1 overflow-hidden border-y border-[#e5e7eb] lg:grid-cols-[minmax(360px,0.8fr)_minmax(520px,1.2fr)]">
          <div
            className="min-h-[320px] overflow-y-auto pr-2 lg:min-h-0"
            aria-label="Student activity timeline"
          >
            {filtered_events.map((event, index) => {
              const selected = selected_event_id === event.id;
              const title = get_event_title(event, is_cms_action);
              const event_date = format_event_date(event.timestamp);
              const previous_date = index
                ? format_event_date(filtered_events[index - 1].timestamp)
                : '';
              const show_date = event_date !== previous_date;
              const context = is_cms_action ? event.performed_by?.name : event.category;

              return (
                <React.Fragment key={event.id}>
                  {show_date ? (
                    <div className="relative flex h-10 items-center pl-10">
                      <span
                        aria-hidden="true"
                        className="absolute bottom-0 left-[17px] top-0 w-px bg-[#d9dee8]"
                      />
                      <span className="absolute left-[13px] z-[1] h-2.5 w-2.5 rounded-full bg-[#cbd5e1] ring-4 ring-white" />
                      <h3 className="m-0 text-sm font-semibold text-slate-700">{event_date}</h3>
                    </div>
                  ) : null}
                  <article className="relative pl-10">
                    {/* Explicit color avoids this rail disappearing in older dashboard themes. */}
                    <span
                      aria-hidden="true"
                      className="absolute bottom-0 left-[17px] top-0 w-px bg-[#d9dee8]"
                    />
                    <span
                      className={`absolute left-[11px] top-[18px] z-[1] w-3.5 h-3.5 rounded-full border-[3px] border-white ring-1 ring-[#4332df] ${
                        is_cms_action ? 'bg-[#4332df]' : 'bg-white'
                      }`}
                    />
                    <div className={`border-b border-slate-200 ${selected ? 'bg-[#f3f1ff]' : ''}`}>
                      <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-[#f8fafc]">
                        <time className="w-20 shrink-0 pt-0.5 text-xs font-medium text-[#667085]">
                          {format_event_time(event.timestamp)}
                        </time>
                        <div className="min-w-0 flex-1">
                          <h4 className="m-0 truncate text-sm font-semibold text-[#172033]">
                            {title}
                          </h4>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#667085]">
                            <span>
                              {is_cms_action ? 'By' : 'Category'}: {display_value(context)}
                            </span>
                            <span aria-hidden="true" className="text-[#cbd5e1]">
                              •
                            </span>
                            <span>Source: {display_value(event.source)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="!flex !shrink-0 !items-center !gap-1 !rounded-md !border-0 !bg-transparent !px-2 !py-1 text-xs !font-semibold !text-[#4332df] hover:!bg-white focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-[#4332df]"
                          aria-pressed={selected}
                          aria-label={`View details for ${title}`}
                          onClick={() => set_selected_event_id(event.id)}
                        >
                          {selected ? 'Viewing' : 'View'}
                          <span aria-hidden="true" className="text-base">
                            ›
                          </span>
                        </button>
                      </div>
                    </div>
                  </article>
                </React.Fragment>
              );
            })}
            {pagination.has_more ? (
              <div className="my-4 text-center">
                <button
                  type="button"
                  className={OUTLINE_BUTTON}
                  disabled={loading_more}
                  onClick={() => load_events(category, current_filters, pagination.page + 1, true)}
                >
                  {loading_more ? 'Loading…' : 'Load More'}
                </button>
              </div>
            ) : null}
          </div>
          <aside
            className="min-h-[360px] overflow-y-auto border-t border-[#e5e7eb] bg-[#f8fafc] lg:min-h-0 lg:border-l lg:border-t-0"
            aria-label="Selected activity details"
          >
            {selected_event ? (
              <div>
                <div className="sticky top-0 z-10 border-b border-[#e5e7eb] bg-white px-5 py-4">
                  <p className="m-0 text-xs font-medium text-[#667085]">
                    {format_event_date(selected_event.timestamp)} ·{' '}
                    {format_event_time(selected_event.timestamp)}
                  </p>
                  <h3 className="mb-0 mt-1 text-lg font-semibold text-[#172033]">
                    {selected_event_title}
                  </h3>
                </div>
                <EventDetails event={selected_event} is_cms_action={is_cms_action} />
              </div>
            ) : (
              <div className="flex h-full min-h-[280px] items-center justify-center px-8 text-center">
                <div>
                  <p className="m-0 font-semibold text-[#344054]">Select an activity</p>
                  <p className="mb-0 mt-1 text-sm text-[#667085]">
                    Its audit details will appear here without expanding the timeline.
                  </p>
                </div>
              </div>
            )}
          </aside>
        </div>
      ) : null}
    </section>
  );
}

StudentActivity.propTypes = {
  student_uuid: PropTypes.string,
  iana_timezone: PropTypes.string
};

StudentActivity.defaultProps = {
  student_uuid: '',
  iana_timezone: 'UTC'
};

export default StudentActivity;
